import React, { forwardRef, useState, useRef, useEffect } from 'react';
import SearchIcon from '../../../Images/search-icon.svg';
import ArrowUpIcon from '../../../Images/arrow-up.svg';
import ArrowDownIcon from '../../../Images/arrow-down.svg';
import WhiteboardIcon from '../../../Images/whiteboard.svg';

import { 
  ToolbarContainer, 
  SearchContainer, 
  SearchInput, 
  SearchCountBadge, 
  ToolbarButton, 
  NavigationButton, 
  ToolbarDivider,
  VideoCallButton,
  PaymentButton,
  WhiteboardButton,
  SignatureButton,
  ServiceQuestionnaireButton,
  ServiceDropdownMenu,
  ServiceDropdownItem
} from '../styled/Toolbar';

// Icon Components
const VideoIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="23 7 16 12 23 17 23 7"></polygon>
    <rect x="1" y="5" width="15" height="14" rx="2" ry="2"></rect>
  </svg>
);

const PaymentIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="1" y="4" width="22" height="16" rx="2" ry="2"></rect>
    <line x1="1" y1="10" x2="23" y2="10"></line>
  </svg>
);

const SignatureIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 17c3.333-3.333 5.667-6.667 7-8 1.333 1.333 3.667 4.667 7 8"></path>
    <path d="M20 21H4"></path>
    <path d="M21 7l-5-5-9 9 5 5 9-9z"></path>
  </svg>
);

const RequestInfoIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2"></path>
    <rect x="9" y="3" width="6" height="4" rx="1"></rect>
    <path d="M9 12h6M9 16h4"></path>
  </svg>
);

const ServiceIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
    <polyline points="14 2 14 8 20 8"></polyline>
    <line x1="16" y1="13" x2="8" y2="13"></line>
    <line x1="16" y1="17" x2="8" y2="17"></line>
    <polyline points="10 9 9 9 8 9"></polyline>
  </svg>
);

const ChevronDownIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="6 9 12 15 18 9"></polyline>
  </svg>
);

// Modern SVG icons for services
const RealEstateIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
    <polyline points="9 22 9 12 15 12 15 22"></polyline>
  </svg>
);

const EmploymentIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect>
    <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path>
  </svg>
);

const EnglishAcademyIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"></path>
    <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"></path>
  </svg>
);

// Service questionnaire options
const SERVICE_OPTIONS = [
  {
    type: 'real_estate',
    icon: <RealEstateIcon />,
    title: 'Real Estate',
    description: 'Housing, rentals, and property search'
  },
  {
    type: 'employment',
    icon: <EmploymentIcon />,
    title: 'Employment',
    description: 'Job search and career assistance'
  },
  {
    type: 'english_academy',
    icon: <EnglishAcademyIcon />,
    title: 'English Academy',
    description: 'Language courses and training'
  }
];

const SearchToolbar = forwardRef(({
  searchText,
  setSearchText,
  searchResults,
  currentResultIndex,
  handleGoToPrevResult,
  handleGoToNextResult,
  handleScrollToTop,
  handleScrollToBottom,
  setShowPaymentDialog,
  setShowNewPaymentRequest,
  isSharedCase,
  onStartVideoCall,
  onStartWhiteboard,
  onRequestSignature,
  onRequestInfo,
  onSendServiceQuestionnaire
}, searchInputRef) => {
  const [showServiceDropdown, setShowServiceDropdown] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowServiceDropdown(false);
      }
    };

    if (showServiceDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showServiceDropdown]);

  const handleSearchChange = (event) => {
    setSearchText(event.target.value);
  };

  const handleServiceSelect = (serviceType) => {
    setShowServiceDropdown(false);
    if (onSendServiceQuestionnaire) {
      onSendServiceQuestionnaire(serviceType);
    }
  };

  return (
    <ToolbarContainer>
      <SearchContainer>
        <img
          src={SearchIcon}
          alt="Search"
          style={{
            width: '16px',
            height: '16px',
            marginRight: '10px',
            filter: 'invert(1)',
            opacity: 0.8
          }}
        />
        <SearchInput
          ref={searchInputRef}
          placeholder="Search in conversation... (Ctrl+F)"
          value={searchText}
          onChange={handleSearchChange}
        />
      </SearchContainer>

      {searchResults.length > 0 && (
        <SearchCountBadge>
          {currentResultIndex + 1}/{searchResults.length}
        </SearchCountBadge>
      )}

      {/* Navigation buttons */}
      <ToolbarButton
        id="toolbar-prev-button"
        disabled={searchResults.length === 0}
        title="Previous result (Shift+F3)"
        onClick={handleGoToPrevResult}
      >
        <img src={ArrowUpIcon} alt="Previous" />
      </ToolbarButton>

      <ToolbarButton
        id="toolbar-next-button"
        disabled={searchResults.length === 0}
        title="Next result (F3)"
        onClick={handleGoToNextResult}
      >
        <img src={ArrowDownIcon} alt="Next" />
      </ToolbarButton>

      <ToolbarDivider />

      <NavigationButton
        id="toolbar-start-button"
        title="Jump to start (Ctrl+Home)"
        onClick={handleScrollToTop}
      >
        <span>Start</span>
      </NavigationButton>

      <NavigationButton
        id="toolbar-end-button"
        title="Jump to end (Ctrl+End)"
        onClick={handleScrollToBottom}
      >
        <span>End</span>
      </NavigationButton>

      <ToolbarDivider />

      {/* Video Call Button */}
      <VideoCallButton
        id="start-video-call-button"
        title="Start a video call"
        onClick={onStartVideoCall}
      >
        <VideoIcon />
        <span>Video</span>
      </VideoCallButton>

      {/* Payments belong to the contractor who owns the case; hide for shared (subcontracted) cases */}
      {!isSharedCase && (
        <PaymentButton
          id="request-payment-button"
          title="Request payment from client"
          onClick={() => setShowNewPaymentRequest ? setShowNewPaymentRequest(true) : setShowPaymentDialog(true)}
        >
          <PaymentIcon />
          <span>Payment</span>
        </PaymentButton>
      )}

      <WhiteboardButton
        id="start-whiteboard-button"
        title="Start a collaborative whiteboard"
        onClick={onStartWhiteboard}
      >
        <img src={WhiteboardIcon} alt="Whiteboard" />
        <span>Board</span>
      </WhiteboardButton>

      <SignatureButton
        id="request-signature-button"
        title="Request digital signature from client"
        onClick={onRequestSignature}
      >
        <SignatureIcon />
        <span>Sign</span>
      </SignatureButton>

      {/* Request information: adviser types/dictates what they need -> AI form -> emailed to the client */}
      {onRequestInfo && (
        <SignatureButton
          id="request-info-button"
          title="Ask the client for information — write or dictate what you need, the AI turns it into a form"
          onClick={onRequestInfo}
          style={{ background: 'rgba(99, 102, 241, 0.15)', borderColor: 'rgba(99, 102, 241, 0.35)' }}
        >
          <RequestInfoIcon />
          <span>Request info</span>
        </SignatureButton>
      )}

      <ToolbarDivider />

      {/* Service Questionnaire Dropdown */}
      <div ref={dropdownRef} style={{ position: 'relative', zIndex: showServiceDropdown ? 9999 : 1 }}>
        <ServiceQuestionnaireButton
          id="service-questionnaire-button"
          onClick={(e) => {
            e.stopPropagation();
            setShowServiceDropdown(!showServiceDropdown);
          }}
        >
          <ServiceIcon />
          <span>Services</span>
          <ChevronDownIcon />
        </ServiceQuestionnaireButton>

        {showServiceDropdown && (
          <ServiceDropdownMenu>
            {SERVICE_OPTIONS.map((service) => (
              <ServiceDropdownItem
                key={service.type}
                onClick={(e) => {
                  e.stopPropagation();
                  handleServiceSelect(service.type);
                }}
              >
                <span className="icon">{service.icon}</span>
                <div className="content">
                  <div className="title">{service.title}</div>
                  <div className="description">{service.description}</div>
                </div>
              </ServiceDropdownItem>
            ))}
          </ServiceDropdownMenu>
        )}
      </div>
    </ToolbarContainer>
  );
});

SearchToolbar.displayName = 'SearchToolbar';

export default SearchToolbar; 